// CareLogix™ ORACLE — Single Source of Truth for Member Session (Phase 39).
//
// The authenticated-member UI was previously reading the session token from
// the wrong localStorage key, which caused signed-in Enterprise members
// (e.g. Richard England) to see the guest homepage. This module is the
// canonical resolver: it scans every legacy key, fetches /api/auth/me first
// (the Phase 23 endpoint actually wired by the AccessLogin flow), then
// /api/membership/me as a secondary, and finally falls back to whatever the
// AuthContext mirrored into localStorage.
//
// Strictly additive. AdminOne JWTs are NOT touched.
import { authStorage } from "@/api/authClient";

const TOKEN_KEYS = [
  "oracle_member_token",
  "session_token",
  "carelogix_member_token",
  "oracle.session_token",
  "oracle.access_token",
  "carelogix.auth.token",
  "oracle.auth.token",
  "access_token",
];

const USER_KEYS = [
  "oracle_member",
  "carelogix_member_user",
  "carelogix_member",
  "member_profile",
];

export function getOracleMemberToken() {
  try {
    // Prefer the canonical authStorage value first to keep AdminOne JWTs
    // strictly out of this code-path.
    const primary = authStorage.getToken && authStorage.getToken();
    if (primary) return primary;
  } catch {
    /* ignore */
  }
  try {
    for (const k of TOKEN_KEYS) {
      const v = localStorage.getItem(k);
      if (v) return v;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function getStoredOracleMember() {
  try {
    for (const k of USER_KEYS) {
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      try {
        return JSON.parse(raw);
      } catch {
        /* fall through */
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

function _coerceTier(t) {
  if (!t) return "guest";
  if (typeof t === "string") return t.toLowerCase();
  if (typeof t === "object") {
    return String(t.id || t.label || t.name || "guest").toLowerCase();
  }
  return "guest";
}

export function normalizeOracleMember(payload) {
  if (!payload) return null;

  // Backend envelopes commonly look like { user, guest } or { member, ... }.
  // Unwrap if present, but preserve top-level fields too (membership/me).
  const user = payload.user || payload.member || payload;
  const guestFlag = payload.guest === true || user?.guest === true;

  const tier =
    _coerceTier(
      user.tier ||
        user.membership_tier ||
        user.plan ||
        payload.tier ||
        payload.tier_id ||
        (guestFlag ? "guest" : null),
    );

  return {
    id: user.id || user.member_id || user.user_id || user.email || null,
    full_name:
      user.full_name ||
      user.name ||
      user.display_name ||
      user.email ||
      "Member",
    email: user.email || "",
    tier,
    guest: guestFlag || tier === "guest",
    raw: user,
  };
}

export async function fetchOracleMemberMe() {
  const token = getOracleMemberToken();
  if (!token) return null;

  const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const endpoints = [
    "/api/auth/me",
    "/api/membership/me",
    "/api/oracle/member/me",
  ];

  for (const path of endpoints) {
    try {
      const res = await fetch(`${baseUrl}${path}`, {
        method: "GET",
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      if (!res.ok) continue;
      const data = await res.json();
      const normalized = normalizeOracleMember(data);
      if (normalized && normalized.id) return normalized;
    } catch {
      continue;
    }
  }

  return normalizeOracleMember(getStoredOracleMember());
}

export function persistOracleMemberSession(token, member) {
  try {
    if (token) {
      // Mirror the token into the legacy + canonical keys so every consumer
      // (membershipClient, oracleMemberSession, AdminOne API guard) sees it.
      localStorage.setItem("oracle_member_token", token);
      localStorage.setItem("carelogix_member_token", token);
    }
    if (member) {
      const normalized = normalizeOracleMember(member) || member;
      localStorage.setItem("oracle_member", JSON.stringify(normalized));
    }
  } catch {
    /* ignore */
  }
}
